import React, { useEffect, useState } from 'react';
import { sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../../firebase/firebaseConfig';
import { useTranslation } from 'react-i18next';
import { showSuccessToast, showErrorToast } from '../Toastify/toastHelper';
import { CircularProgress } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { ReactComponent as CloseIcon } from '../../assets/Close-btn.svg';
import { UserService } from '../../services/UserService';
import { useAppContext } from '../../AppContext';

interface ForgotPasswordProps {
  onClose?: () => void;
}

const ForgotPassword: React.FC<ForgotPasswordProps> = ({ onClose }) => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAppContext();

  useEffect(() => {
    if(user){
      setEmail(user.email);
    }
  }, [user]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const isAvailable = await checkEmailAvailability();
    if (!isAvailable) {
      showErrorToast(t('auth.emailNotAvailable'), t);
      return;
    }
    
    if (!email) {
      showErrorToast(t('auth.validationError'), t);
      return;
    }

    setLoading(true);
    try {
      await sendPasswordResetEmail(auth, email);
      showSuccessToast(t('auth.resetEmailSent'), t);
      navigate('/');
    } catch (error: any) {
      if (error.code === 'auth/invalid-email') {
        showErrorToast(t('auth.invalidEmail'), t);
      } else if (error.code === 'auth/user-not-found') {
        showErrorToast(t('auth.userNotFound'), t);
      } else {
        showErrorToast(t('auth.resetEmailError'), t);
      }
    } finally {
      setLoading(false);
    }
  };

  const checkEmailAvailability = async () => {
    const res = await UserService.checkEmailAvailability(email);
    return res.available;
  }

  return (
    <div className="items-center justify-center bg-gray-50 px-4 sm:px-6 lg:px-8 pb-5">
        <p className="mt-2 text-center text-base text-gray-600 mb-[40px]">
          {t('auth.forgotPasswordDescription')}
        </p>
        <form className="space-y-6" onSubmit={handleSubmit}>
          <div className="flex flex-col">
            <label htmlFor="email-address" className="font-normal md:text-[18px] mb-[7px] leading-none">
              {t('auth.email')}
            </label>
            <input
              id="email-address"
              name="email"
              type="email"
              autoComplete="email"
              disabled={user ? true : false}
              required
              className="input border-[1px] rounded-[5px] border-[rgba(102,102,102,0.35)] px-[12px] md:px-[20px] pt-[11px] md:pt-[15px] pb-[12px] md:pb-[14px] !important text-left"
              placeholder={t('auth.emailPlaceholder')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="bg-[#00726B] font-semibold text-[18px] md:text-[22px] w-full text-white rounded-[12px] md:rounded-[5px] mt-[10px] py-[18px] md:py-[10px]"
          >
            {loading ? (
              <CircularProgress size={24} style={{ color: '#ffffff' }} />
            ) : (
              t('auth.sendResetLink')
            )}
          </button>
        </form>
      </div>
  );
};

export default ForgotPassword; 